import LanesChart from "@core/charts/LanesChart/index.jsx";
import ContactDetails from "@shared/profile-components/ContactDetails.jsx";
import PortsChart from "@core/charts/PortsChart/index.jsx";
import CountryProfileChart from "@core/charts/CountryProfileChart/index.jsx";
import { toast } from "react-toastify";
import Tabs from "@containers/PortProfile/components/Tabs";
import PortOverview from "@containers/PortProfile/components/PortOverview";
import PortTable from "@containers/PortProfile/components/PortTable";
import LogisticsOverView from "@containers/LogisticsProfile/components/LogisticsOverView";
import LogisticsTable from "@containers/LogisticsProfile/components/LogisticsTable";
import { eventNameEnum, isValidStringRegex, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum";
import { store } from "@redux/store";
import { redirectingProfileEnum } from "@core/global/global-country-dictionary";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import ReactTooltip from "react-tooltip";

const targetSVG = "M9,0C4.029,0,0,4.029,0,9s4.029,9,9,9s9-4.029,9-9S13.971,0,9,0z M9,15.93 c-3.83,0-6.93-3.1-6.93-6.93S5.17,2.07,9,2.07s6.93,3.1,6.93,6.93S12.83,15.93,9,15.93 M12.5,9c0,1.933-1.567,3.5-3.5,3.5S5.5,10.933,5.5,9S7.067,5.5,9,5.5 S12.5,7.067,12.5,9z";

export const renderTabs = (activeTabId) => {
  let renderTabs = {
    overview: <PortOverview />,
    freight_forwarder: <PortTable tableName="Freight Forwarders" />,
    vocc: <PortTable tableName="VOCCs" />,
    nvocc: <PortTable tableName="NVOCCs" />,
    vessel: <PortTable tableName="Vessels" />,
    importers: <PortTable tableName="Consignees" />,
    exporters: <PortTable tableName="Shippers" />,
  };

  return renderTabs[activeTabId];
};

export const renderPortPanelData = (activePanelId, countDetails = {}) => {
  let renderPortPanelData = {
    port_of_lading: <Tabs />,
    port_of_unlading: <Tabs />,
    contact_details: <ContactDetails profileType={profileTypeEnum.ports} countDetails={countDetails} />,
  };

  return renderPortPanelData[activePanelId];
};

export const renderLogisticsPanelData = (activeTabId, countDetails = {}) => {
  let renderLogisticsPanelData = {
    contact_details: <ContactDetails eventEnum={trackingEventDictionary.logisticsCompanyProfilePageSearchContactsTabSearchContacts} profileType={profileTypeEnum.logistics} countDetails={countDetails} />,
    overview: <LogisticsOverView />,
    importers: <LogisticsTable tableName="Consignees" />,
    exporters: <LogisticsTable tableName="Shippers" />,
  };

  return renderLogisticsPanelData[activeTabId];
};

export const getChartType = (activeCategory, mapData = [], loading = false, error = false, imageData = [], homeGeoPoint = {}) => {
  switch (activeCategory) {
    case "trade_country":
      return <CountryProfileChart mapData={mapData} loading={loading} error={error} />;
    case "trade_port":
      return <PortsChart mapData={mapData} loading={loading} error={error} imageData={imageData} />;
    case "trade_lane":
      return <LanesChart loading={loading} error={error} mapData={mapData} imageData={imageData} homeGeoPoint={homeGeoPoint} />;
    default:
      return <LanesChart loading={loading} error={error} mapData={mapData} imageData={imageData} homeGeoPoint={homeGeoPoint} />;
  }
};

export const transformData = (data, isTitleCase, isUpperCase) => {
  if (Array.isArray(data)) {
    if (data.length <= 3)
      return data
        .join(", ")
        .toLowerCase()
        .split(" ")
        .reduce((s, c) => s + "" + (c.charAt(0).toUpperCase() + c.slice(1) + " "), "");
    else
      return (
        <span>
          <ReactTooltip id={"porttype"} />
          <span
            className="text-capitalize"
            data-tip={data
              .join(", ")
              .toLowerCase()
              .split(" ")
              .reduce((s, c) => s + "" + (c.charAt(0).toUpperCase() + c.slice(1) + " "), "")}
            data-for={"porttype"}>
            {data.slice(0, 3).join(", ") + `  +${data.length - 3}`}
          </span>
        </span>
      );
  }
  if (typeof data !== "string" || data.length === 0 || !isValidStringRegex.test(data)) {
    return false;
  }

  return isTitleCase
    ? data
        .toLowerCase()
        .split(" ")
        .reduce((s, c) => s + "" + (c.charAt(0).toUpperCase() + c.slice(1) + " "), "")
    : isUpperCase
      ? data.toUpperCase()
      : data;
};

export const handleCopy = async (value) => {
  try {
    const permissions = await navigator.permissions.query({ name: "clipboard-write" });
    if (permissions.state === "granted" || permissions.state === "prompt") {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard!");
    } else {
      throw new Error("Can't access the clipboard. Check your browser permissions.");
    }
  } catch (error) {
    toast.error("Error copying to clipboard:");
  }
};

export const getDisplayCount = (number, decPlaces) => {
  decPlaces = Math.pow(10, decPlaces);
  // Enumerate number abbreviations
  let abbrev = ["k", "m", "b", "t"];
  // Go through the array backwards, so we do the largest first
  for (let i = abbrev.length - 1; i >= 0; i--) {
    // Convert array index to "1000", "1000000", etc
    let size = Math.pow(10, (i + 1) * 3);
    // If the number is bigger or equal do the abbreviation
    if (size <= number) {
      // Here, we multiply by decPlaces, round, and then divide by decPlaces.
      // This gives us nice rounding to a particular decimal place.
      number = Math.round((number * decPlaces) / size) / decPlaces;
      // Handle special case where we round up to the next abbreviation
      if (number === 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }
      // Add the letter for the abbreviation
      number += abbrev[i];
      // We are done... stop
      break;
    }
  }
  return number;
};

export function isValidCoordinates(...args) {
  return args.filter((item) => typeof item !== "number").length > 0 ? false : true;
}

const getTradeCountryChartData = (mapData) => {
  let data = [];
  if (mapData && Array.isArray(mapData)) {
    for (let i = 0; i < mapData.length; i++) {
      if (mapData[i] && mapData[i].countryCode && mapData[i].doc_count) {
        let obj = {};
        obj.id = mapData[i].countryCode;
        obj.value = parseInt(mapData[i].doc_count);
        data.push({ ...obj });
      }
    }
  }
  return {
    mapData: data,
    imageData: [],
  };
};

const getTradeLanesLogisticsChartData = (mapData) => {
  let data = [];
  let imageData = [];
  let imageDataFrom = new Set();
  if (mapData && Array.isArray(mapData)) {
    for (let i = 0; i < mapData.length; i++) {
      const { doc_count } = mapData[i];
      const { key: fromKey, countryCode, latitude: fromLatitude, longitude: fromLongitude } = mapData[i].from;
      const { latitude: toLatitude, longitude: toLongitude, key: toKey } = mapData[i].to;
      if (isValidCoordinates(fromLatitude, fromLongitude, toLongitude, toLatitude) && doc_count > 0) {
        let obj = {
          portName: transformData(toKey, true, false),
          primaryPortName: transformData(fromKey, true, false),
          shipments: parseInt(doc_count),
        };
        obj.id = countryCode;
        obj.multiGeoLine = [
          [
            { latitude: fromLatitude, longitude: fromLongitude },
            { latitude: toLatitude, longitude: toLongitude },
          ],
        ];
        obj.shipments = parseInt(doc_count);
        data.push({ ...obj });
        let imgDataObj = {
          ...obj,
          id: toKey,
          svgPath: targetSVG,
          title: transformData(toKey, true, false),
          latitude: toLatitude,
          longitude: toLongitude,
          scale: 1,
          radius: 6,
          primaryPortName: null,
          shipments: null,
        };
        let imgDataObjFrom = {
          id: fromKey,
          svgPath: targetSVG,
          title: transformData(fromKey, true, false),
          latitude: fromLatitude,
          longitude: fromLongitude,
          scale: 1,
          radius: 6,
        };
        imageData.push({ ...imgDataObj });
        const stringifiedItem = JSON.stringify(imgDataObjFrom);
        imageDataFrom.add(stringifiedItem);
      }
    }
  }
  const fromArr = Array.from(imageDataFrom).map((item) => JSON.parse(item));
  imageData.push(...fromArr);
  return {
    mapData: data,
    imageData,
  };
};

const getTradeLanesPortsChartData = (mapData = [], primaryDetails = {}, activePanel) => {
  let data = [];
  let imageData = [];
  let homeGeoPoint = {};
  if (mapData && Array.isArray(mapData)) {
    const { latitude: primaryLatitude, longitude: primaryLongitude, portName, shipments: primaryShipments } = primaryDetails;
    if (isValidCoordinates(primaryLatitude, primaryLongitude)) {
      for (let i = 0; i < mapData.length; i++) {
        const { key, _id, doc_count, countryCode, latitude, longitude } = mapData[i];
        if (key && _id && doc_count && isValidCoordinates(latitude, longitude)) {
          let lineObj = {};
          lineObj.id = countryCode;
          lineObj.shipments = parseInt(doc_count);
          lineObj.portName = activePanel === "port_of_unlading" ? transformData(portName, true, false) : transformData(key, true, false);
          lineObj.primaryPortName = activePanel === "port_of_unlading" ? transformData(key, true, false) : transformData(portName, true, false);
          lineObj.multiGeoLine =
            activePanel === "port_of_unlading"
              ? [
                  [
                    { latitude, longitude },
                    { latitude: primaryLatitude, longitude: primaryLongitude },
                  ],
                ]
              : [
                  [
                    { latitude: primaryLatitude, longitude: primaryLongitude },
                    { latitude, longitude },
                  ],
                ];
          data.push({ ...lineObj });
          let imgDataObj = {
            ...lineObj,
            id: key,
            svgPath: targetSVG,
            title: transformData(key, true, false),
            latitude,
            longitude,
            scale: 1,
            radius: 6,
          };
          imageData.push({ ...imgDataObj });
        }
      }
      const imageFromData = {
        id: portName,
        svgPath: targetSVG,
        title: transformData(portName, true, false),
        latitude: primaryLatitude,
        longitude: primaryLongitude,
        scale: 2,
        shipments: parseInt(primaryShipments),
        radius: 12,
      };
      imageData.push({ ...imageFromData });
    }
    homeGeoPoint = {
      latitude: primaryLatitude,
      longitude: primaryLongitude,
    };
  }
  return {
    mapData: data,
    imageData,
    homeGeoPoint,
  };
};

const getScaleValue = (shipment, maxValue) => {
  const minScale = 0.8;
  const maxScale = 1.8;

  if (shipment >= maxValue) {
    return 1.8;
  }

  // Calculate the scale based on the ratio
  const scale = minScale + (maxScale - minScale) * (shipment / maxValue);
  // Ensure the scale stays within the specified range
  return Math.min(maxScale, Math.max(minScale, scale));
};

export const getTradePortsChartData = (mapData) => {
  let data = [];
  if (mapData && Array.isArray(mapData)) {
    const maxDocCountObject = mapData.reduce(
      (max, current) => {
        return current.doc_count > max.doc_count ? current : max;
      },
      { doc_count: -Infinity }
    );
    for (let i = 0; i < mapData.length; i++) {
      const { countryCode, doc_count, key, latitude, longitude } = mapData[i];
      if (key && countryCode && doc_count) {
        let obj = {};
        obj.id = key;
        obj.svgPath = targetSVG;
        obj.latitude = latitude;
        obj.longitude = longitude;
        obj.title = transformData(key, true, false);
        obj.scale = getScaleValue(parseInt(doc_count), maxDocCountObject.doc_count);
        obj.shipments = parseInt(doc_count);
        data.push({ ...obj });
      }
    }
  }
  return {
    mapData: data,
    imageData: [],
  };
};

export const prepareLogisticsMapData = (category, data) => {
  switch (category) {
    case "trade_country":
      return getTradeCountryChartData(data);
    case "trade_lane":
      return getTradeLanesLogisticsChartData(data);
    case "trade_port":
      return getTradePortsChartData(data);
    default:
      return {
        mapData: [],
        imageData: [],
      };
  }
};

export const preparePortsMapData = (category, data, primaryDetails = {}, activePanel = "port_of_lading") => {
  switch (category) {
    case "trade_country":
      return getTradeCountryChartData(data);
    case "trade_lane":
      return getTradeLanesPortsChartData(data, primaryDetails, activePanel);
    default:
      return {
        mapData: [],
        imageData: [],
      };
  }
};

export const isProfileAccess = (profileType) => {
  const {
    userDetails: {
      creditDetails: { planAccess },
    },
  } = store.getState() || {};
  const redirectionUrl = redirectingProfileEnum[profileType]?.redirectionType;
  const isLogisticProfile = planAccess?.is_logistic_profile ?? false;
  const isPortProfile = planAccess?.is_port_profile ?? false;
  const isTradeLaneProfile = planAccess?.is_tradelane_profile ?? false;
  const isHSCodeProfile = planAccess?.is_hscode_profile ?? false;
  return {
    logistics: isLogisticProfile && redirectionUrl ? true : false,
    port: isPortProfile && redirectionUrl ? true : false,
    tradeLane: isTradeLaneProfile && redirectionUrl ? true : false,
    hsCode: isHSCodeProfile && redirectionUrl ? true : false,
    redirectionUrl,
  };
};

export const getUserActivePlan = () => {
  const {
    userDetails: {
      userData: { planIds },
    },
  } = store.getState() || {};
  if (planIds && Array.isArray(planIds) && planIds.length > 0) {
    return planIds[0].planId;
  }
  return "";
};

export const getEventName = (profileType, eventName) => {
  const eventNameModified = eventName || "";
  switch (profileType) {
    case profileTypeEnum.ports:
      return eventNameEnum.ports + eventNameModified;
    case profileTypeEnum.logistics:
      return eventNameEnum.logistics + eventNameModified;
    case profileTypeEnum.tradeLanes:
      return eventNameEnum.tradeLanes + eventNameModified;
    case profileTypeEnum.company:
      return eventNameEnum.company + eventNameModified;
  }
};
